/**
 * Shared website booking cart helpers.
 * One cart (services + per-service professional) for both service-first and professional-first entry.
 */

export const BOOKING_CART_STORAGE_KEY = 'servicesBookingSummary'

export const readBookingCart = () => {
  try {
    const raw = localStorage.getItem(BOOKING_CART_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export const findCategoryForService = (categories, service) => {
  if (!service) return null
  if (service.category && (service.category.id || service.category.name)) {
    return service.category
  }
  const categoryId = service.category_id || service.categoryId
  if (categoryId && Array.isArray(categories)) {
    const byId = categories.find((cat) => String(cat.id) === String(categoryId))
    if (byId) return byId
  }
  if (!Array.isArray(categories)) return null
  return (
    categories.find((cat) => cat.services?.some((s) => String(s.id) === String(service.id))) ||
    null
  )
}

export const normalizeProfessionalForCart = (professional) => {
  if (!professional) return null
  return {
    ...professional,
    image: professional.image || professional.image_url || null,
    image_url: professional.image_url || professional.image || null,
    role: professional.role || professional.specialty || null,
    specialty: professional.specialty || professional.role || null,
  }
}

export const buildCartItem = (service, professional, category = null) => {
  const pro = normalizeProfessionalForCart(professional)
  const cat = category || service?.category || null
  return {
    service: {
      ...service,
      category: cat,
      category_id: service.category_id || cat?.id,
    },
    professional: pro,
    category: cat
      ? {
          id: cat.id,
          name: cat.name,
        }
      : null,
  }
}

/** Merge incoming cart items; same service id is replaced (reassigned). */
export const mergeCartItems = (existing = [], incoming = []) => {
  const map = new Map()
  existing.forEach((item) => {
    if (item?.service?.id != null) map.set(String(item.service.id), item)
  })
  incoming.forEach((item) => {
    if (item?.service?.id != null) map.set(String(item.service.id), item)
  })
  return Array.from(map.values())
}

export const removeCartService = (existing = [], serviceId) =>
  existing.filter((item) => String(item.service?.id) !== String(serviceId))

export const cartToBookingServices = (summary = []) =>
  summary.map((item) => ({
    ...item.service,
    professional: normalizeProfessionalForCart(item.professional),
    selectedProfessional: normalizeProfessionalForCart(item.professional),
    professional_id: item.professional?.id,
    categoryName: item.category?.name || item.service?.category?.name,
    category_id: item.service?.category_id || item.category?.id,
  }))

export const resolveCartSpecialist = (summary = []) => {
  if (!summary.length) return null
  const ids = [...new Set(summary.map((item) => item.professional?.id).filter(Boolean))]
  if (ids.length === 1) {
    return normalizeProfessionalForCart(summary[0].professional)
  }
  return { id: 'any', name: 'Any Specialist', image: null, image_url: null }
}

export const dispatchMergeBookingCart = (items) => {
  if (!Array.isArray(items) || items.length === 0) return
  const existing = readBookingCart()
  const merged = mergeCartItems(existing, items)
  try {
    localStorage.setItem(BOOKING_CART_STORAGE_KEY, JSON.stringify(merged))
  } catch {
    // no-op
  }
  window.dispatchEvent(
    new CustomEvent('mergeServicesBookingSummary', { detail: items }),
  )
  window.dispatchEvent(
    new CustomEvent('restoreServicesBookingSummary', { detail: merged }),
  )
}

export const dispatchRemoveFromBookingCart = (serviceId) => {
  if (serviceId == null) return
  const existing = readBookingCart()
  const next = removeCartService(existing, serviceId)
  try {
    if (next.length > 0) {
      localStorage.setItem(BOOKING_CART_STORAGE_KEY, JSON.stringify(next))
    } else {
      localStorage.removeItem(BOOKING_CART_STORAGE_KEY)
    }
  } catch {
    // no-op
  }
  window.dispatchEvent(
    new CustomEvent('removeFromServicesBookingSummary', { detail: { serviceId } }),
  )
}

export const dispatchRestoreBookingCart = (summary) => {
  window.dispatchEvent(
    new CustomEvent('restoreServicesBookingSummary', { detail: summary || [] }),
  )
}
